import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { API_URL } from '../config';
import { formatDate } from '../utils/formatDate';
import { situacaoExameLabel, situacaoExameStyle } from '../utils/situacaoExame';
import './PainelInconsistencias.css';

function getDateStr(date) {
  return date.toISOString().split('T')[0];
}

function get90DaysAgoStr() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return getDateStr(d);
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const AGING_COLORS = {
  '0_2d': '#10b981',
  '3_7d': '#f59e0b',
  '8_15d': '#f97316',
  '16_30d': '#ef4444',
  '30d_mais': '#7f1d1d'
};


// formatDate importado de ../utils/formatDate

const headerStyles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
};

function PainelInconsistencias() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [dataInicio, setDataInicio] = useState(get90DaysAgoStr());
  const [dataFim, setDataFim] = useState(getDateStr(new Date()));
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroSeveridade, setFiltroSeveridade] = useState(null);
  const [filtroColeta, setFiltroColeta] = useState('todos');
  const [openSections, setOpenSections] = useState({
    statusExames: true,
    divergencias: true,
    financeiro: true,
    entregas: false,
    cadastro: false,
    notificacoes: false,
    integracao: false,
    padronizacao: false
  });

  // Sub-tabs per section
  const [subTabStatusExames, setSubTabStatusExames] = useState('parciais');
  const [subTabDivergencias, setSubTabDivergencias] = useState('liberadoSemExame');
  const [subTabFinanceiro, setSubTabFinanceiro] = useState('semPagamento');
  const [subTabEntregas, setSubTabEntregas] = useState('prontosSemEntrega');
  const [subTabCadastro, setSubTabCadastro] = useState('semTelefone');
  const [subTabNotificacoes, setSubTabNotificacoes] = useState('smsPendente');
  const [subTabIntegracao, setSubTabIntegracao] = useState('semAssinatura');
  const [subTabPadronizacao, setSubTabPadronizacao] = useState('comSufixoLocal');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/inconsistencias`, {
        params: { dataInicio, dataFim, coleta: filtroColeta }
      });
      setDados(res.data);
    } catch (err) {
      console.error('Erro ao buscar inconsistencias:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, filtroColeta]);

  useEffect(() => {
    fetchData();
  }, [filtroColeta]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSeveridadeFilter = (tipo) => {
    setFiltroSeveridade(prev => prev === tipo ? null : tipo);
  };

  // Determine which sections are visible based on severity filter
  const isSectionVisible = (section) => {
    if (!filtroSeveridade || !dados) return true;
    const d = dados[section];
    if (!d) return false;
    if (filtroSeveridade === 'critica') return (d.criticas || 0) > 0;
    if (filtroSeveridade === 'alerta') return (d.alertas || 0) > 0;
    if (filtroSeveridade === 'info') return (d.info || 0) > 0;
    return true;
  };

  // Build aging chart data from contagens
  const buildAgingChartData = () => {
    if (!dados?.statusExames?.contagens) return [];
    return dados.statusExames.contagens.map(c => ({
      situacao: situacaoExameLabel(c.SITUACAO),
      '0-2d': c['0_2d'] || 0,
      '3-7d': c['3_7d'] || 0,
      '8-15d': c['8_15d'] || 0,
      '16-30d': c['16_30d'] || 0,
      '30d+': c['30d_mais'] || 0,
      total: c.TOTAL || 0
    }));
  };


  // Generic table render for solicitacao items
  const renderSolicitacaoTable = (items, showExame = false, showDias = false, diasLabel = 'Dias', showColhido = false) => {
    if (!items || items.length === 0) return <div className="incons-empty">Nenhum item encontrado</div>;
    return (
      <div className="incons-table-container">
        <table className="incons-table">
          <thead>
            <tr>
              <th>Protocolo</th>
              <th>Local</th>
              <th>Data</th>
              <th>Paciente</th>
              {showExame && <th>Exame</th>}
              {showColhido && <th>Colhido</th>}
              {showDias && <th>{diasLabel}</th>}
              {items[0]?.SITUACAO !== undefined && <th>Situacao</th>}
              {items[0]?.TIPO !== undefined && <th>Tipo</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                <td>
                  <span
                    className="link-cell"
                    onClick={() => navigate(`/resultados/${item.SOLICITACAO_ID}`)}
                  >
                    {item.PROTOCOLO}
                  </span>
                </td>
                <td>{item.LOCAL}</td>
                <td>{formatDate(item.DATA)}</td>
                <td>
                  {item.PACIENTE_ID ? (
                    <span
                      className="link-cell"
                      onClick={() => navigate(`/pacientes/${item.PACIENTE_ID}`)}
                    >
                      {item.PACIENTE_NOME || '-'}
                    </span>
                  ) : (item.PACIENTE_NOME || '-')}
                </td>
                {showExame && <td>{item.EXAME || '-'}</td>}
                {showColhido && (
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
                      background: item.MATERIALCOLHIDO === 'T' ? '#dcfce7' : '#fee2e2',
                      color: item.MATERIALCOLHIDO === 'T' ? '#166534' : '#991b1b'
                    }}>
                      {item.MATERIALCOLHIDO === 'T' ? 'Sim' : 'Nao'}
                    </span>
                  </td>
                )}
                {showDias && <td><strong>{item.DIAS_PARADO || item.DIAS_PRONTO || item.DIAS_PENDENTE || item.DIAS_VENCIDO || '-'}</strong></td>}
                {item.SITUACAO !== undefined && (
                  <td>
                    <span className={`severity-badge ${['M','P'].includes(item.SITUACAO) ? 'critica' : 'alerta'}`}>
                      {situacaoExameLabel(item.SITUACAO)}
                    </span>
                  </td>
                )}
                {item.TIPO !== undefined && <td>{item.TIPO}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Paciente table (different structure)
  const renderPacienteTable = (items) => {
    if (!items || items.length === 0) return <div className="incons-empty">Nenhum item encontrado</div>;
    return (
      <div className="incons-table-container">
        <table className="incons-table">
          <thead>
            <tr>
              <th>Paciente</th>
              <th>CPF</th>
              <th>Ultima Solicitacao</th>
              <th>Tipo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                <td>
                  <span
                    className="link-cell"
                    onClick={() => navigate(`/pacientes/${item.PACIENTE_ID}`)}
                  >
                    {item.PACIENTE_NOME || '-'}
                  </span>
                </td>
                <td>{item.CPF || '-'}</td>
                <td>{formatDate(item.ULTIMA_SOLICITACAO)}</td>
                <td>{item.TIPO}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagamentoDivergenteTable = (items) => {
    if (!items || items.length === 0) return <div className="incons-empty">Nenhum item encontrado</div>;
    return (
      <div className="incons-table-container">
        <table className="incons-table">
          <thead>
            <tr>
              <th>Protocolo</th>
              <th>Local</th>
              <th>Data</th>
              <th>Paciente</th>
              <th>Valor Cobrado</th>
              <th>Valor Pago</th>
              <th>Diferenca</th>
              <th>Formas Pgto</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const diff = Number(item.DIFERENCA || 0);
              const isParcial = diff < 0;
              return (
                <tr key={idx} onClick={() => navigate(`/atendimentos/${item.SOLICITACAO_ID}`)} style={{ cursor: 'pointer' }}>
                  <td><strong>{item.LOCAL}-{item.PROTOCOLO}</strong></td>
                  <td>{item.LOCAL}</td>
                  <td>{formatDate(item.DATA)}</td>
                  <td>{item.PACIENTE_NOME || '-'}</td>
                  <td>R$ {Number(item.VALOR_COBRADO || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  <td>R$ {Number(item.VALOR_PAGO || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  <td style={{ color: isParcial ? '#dc2626' : '#f59e0b', fontWeight: 'bold' }}>
                    R$ {Math.abs(diff).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    {isParcial ? ' (falta)' : ' (excede)'}
                  </td>
                  <td><small>{item.FORMAS_PAGAMENTO || '-'}</small></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const scrollToHelp = (key, e) => {
    e.stopPropagation();
    const el = document.getElementById(`help-${key}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Section component
  const renderSection = (key, number, title, content, criticas = 0, alertas = 0, info = 0) => {
    if (!isSectionVisible(key)) return null;
    const isOpen = openSections[key];
    return (
      <div className={`incons-section ${isOpen ? 'open' : ''}`} key={key}>
        <div className="incons-section-header" onClick={() => toggleSection(key)}>
          <span className="section-toggle">{isOpen ? '\u25BC' : '\u25B6'}</span>
          <span className="section-title">{number}. {title}</span>
          <span className="section-help-link" onClick={(e) => scrollToHelp(key, e)} title="Ver explicacao detalhada">?</span>
          <div className="section-badges">
            {criticas > 0 && <span className="badge critica">{criticas}</span>}
            {alertas > 0 && <span className="badge alerta">{alertas}</span>}
            {info > 0 && <span className="badge info">{info}</span>}
          </div>
        </div>
        {isOpen && <div className="incons-section-content">{content}</div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="inconsistencias-page">
        <div style={headerStyles.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={headerStyles.pageIcon}>⚠️</span>
            <div>
              <h1 style={headerStyles.pageTitle}>Painel de Inconsistências</h1>
              <p style={headerStyles.pageSubtitle}>Monitoramento de anomalias operacionais</p>
            </div>
          </div>
        </div>
        <div className="incons-loading">
          <div className="spinner"></div>
          Carregando dados...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inconsistencias-page">
        <div style={headerStyles.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={headerStyles.pageIcon}>⚠️</span>
            <div>
              <h1 style={headerStyles.pageTitle}>Painel de Inconsistências</h1>
              <p style={headerStyles.pageSubtitle}>Monitoramento de anomalias operacionais</p>
            </div>
          </div>
        </div>
        <div className="incons-error">Erro ao carregar: {error}</div>
      </div>
    );
  }

  const resumo = dados?.resumo || { total: 0, criticas: 0, alertas: 0, info: 0 };
  const agingData = buildAgingChartData();

  return (
    <div className="inconsistencias-page">
      <div style={headerStyles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={headerStyles.pageIcon}>⚠️</span>
          <div>
            <h1 style={headerStyles.pageTitle}>Painel de Inconsistências</h1>
            <p style={headerStyles.pageSubtitle}>Monitoramento de anomalias operacionais</p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="incons-filter-bar">
        <label>
          Data Inicio
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </label>
        <label>
          Data Fim
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </label>
        <label>
          Material Colhido
          <select value={filtroColeta} onChange={e => setFiltroColeta(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="colhidos">Somente colhidos</option>
            <option value="naoColhidos">Somente nao colhidos</option>
          </select>
        </label>
        <div className="filter-actions">
          <button className="btn-buscar" onClick={fetchData} disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="incons-summary">
        <div
          className={`incons-summary-card critica ${filtroSeveridade === 'critica' ? 'active' : ''}`}
          onClick={() => handleSeveridadeFilter('critica')}
        >
          <span className="card-value">{resumo.criticas}</span>
          <span className="card-label">Criticas</span>
        </div>
        <div
          className={`incons-summary-card alerta ${filtroSeveridade === 'alerta' ? 'active' : ''}`}
          onClick={() => handleSeveridadeFilter('alerta')}
        >
          <span className="card-value">{resumo.alertas}</span>
          <span className="card-label">Alertas</span>
        </div>
        <div
          className={`incons-summary-card info ${filtroSeveridade === 'info' ? 'active' : ''}`}
          onClick={() => handleSeveridadeFilter('info')}
        >
          <span className="card-value">{resumo.info}</span>
          <span className="card-label">Informativas</span>
        </div>
        <div
          className={`incons-summary-card total ${filtroSeveridade === null ? '' : ''}`}
          onClick={() => setFiltroSeveridade(null)}
        >
          <span className="card-value">{resumo.total}</span>
          <span className="card-label">Total</span>
        </div>
      </div>

      {/* 1. Status de Exames Parados */}
      {renderSection('statusExames', 1, 'Status de Exames Parados',
        <div>
          {agingData.length > 0 && (
            <div className="incons-chart-container" style={{ marginBottom: '1rem' }}>
              <h4>Aging por Situacao</h4>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                <BarChart data={agingData} layout="vertical" margin={{ left: isMobile ? 10 : 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="situacao" width={isMobile ? 65 : 90} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="0-2d" stackId="a" fill={AGING_COLORS['0_2d']} name="0-2 dias" />
                  <Bar dataKey="3-7d" stackId="a" fill={AGING_COLORS['3_7d']} name="3-7 dias" />
                  <Bar dataKey="8-15d" stackId="a" fill={AGING_COLORS['8_15d']} name="8-15 dias" />
                  <Bar dataKey="16-30d" stackId="a" fill={AGING_COLORS['16_30d']} name="16-30 dias" />
                  <Bar dataKey="30d+" stackId="a" fill={AGING_COLORS['30d_mais']} name="30+ dias" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="sub-tabs">
            {[
              { key: 'parciais', label: 'Parciais (M)', items: dados?.statusExames?.itens?.parciais },
              { key: 'pendentes', label: 'Pendentes (P)', items: dados?.statusExames?.itens?.pendentes },
              { key: 'coletados', label: 'Coletados (C)', items: dados?.statusExames?.itens?.coletados },
              { key: 'digitados', label: 'Digitados (D)', items: dados?.statusExames?.itens?.digitados },
              { key: 'faltaConferir', label: 'Falta Conferir (F)', items: dados?.statusExames?.itens?.faltaConferir }
            ].map(tab => (
              <button
                key={tab.key}
                className={`sub-tab ${subTabStatusExames === tab.key ? 'active' : ''}`}
                onClick={() => setSubTabStatusExames(tab.key)}
              >
                {tab.label}
                <span className="tab-count">({(tab.items || []).length})</span>
              </button>
            ))}
          </div>

          {renderSolicitacaoTable(
            dados?.statusExames?.itens?.[subTabStatusExames] || [],
            true, true, 'Dias Parado', true
          )}
        </div>,
        dados?.statusExames?.criticas || 0,
        dados?.statusExames?.alertas || 0
      )}

      {/* 2. Divergencias de Status */}
      {renderSection('divergencias', 2, 'Divergencias de Status',
        <div>
          <div className="mini-cards">
            {[
              { key: 'liberadoSemExame', label: 'Liberado sem exame pronto', total: dados?.divergencias?.totais?.liberadoSemExame || 0, color: 'red' },
              { key: 'prontoSemLiberacao', label: 'Exame pronto sem liberacao', total: dados?.divergencias?.totais?.prontoSemLiberacao || 0, color: 'red' },
              { key: 'coletadoSemProcessamento', label: 'Coletado sem processamento', total: dados?.divergencias?.totais?.coletadoSemProcessamento || 0, color: 'red' },
              { key: 'resultadoSemStatus', label: 'Resultado sem status', total: dados?.divergencias?.totais?.resultadoSemStatus || 0, color: 'orange' },
              { key: 'statusSemResultado', label: 'Status sem resultado', total: dados?.divergencias?.totais?.statusSemResultado || 0, color: 'orange' },
              { key: 'processadoSemColeta', label: 'Processado sem coleta registrada', total: dados?.divergencias?.totais?.processadoSemColeta || 0, color: 'orange' }
            ].map(card => (
              <div
                key={card.key}
                className={`mini-card ${card.color} ${subTabDivergencias === card.key ? 'active' : ''}`}
                onClick={() => setSubTabDivergencias(card.key)}
              >
                <div className="mini-card-value">{card.total}</div>
                <div className="mini-card-label">{card.label}</div>
              </div>
            ))}
          </div>

          {renderSolicitacaoTable(
            dados?.divergencias?.itens?.[subTabDivergencias] || [],
            subTabDivergencias === 'resultadoSemStatus' || subTabDivergencias === 'statusSemResultado' || subTabDivergencias === 'coletadoSemProcessamento' || subTabDivergencias === 'processadoSemColeta',
            subTabDivergencias === 'coletadoSemProcessamento',
            'Dias Parado'
          )}
        </div>,
        dados?.divergencias?.criticas || 0,
        dados?.divergencias?.alertas || 0
      )}

      {/* 3. Financeiro */}
      {renderSection('financeiro', 3, 'Financeiro',
        <div>
          <div className="sub-tabs">
            {[
              { key: 'semPagamento', label: 'Sem Pagamento', total: dados?.financeiro?.totais?.semPagamento || 0 },
              { key: 'pagamentoDivergente', label: 'Pgto Divergente', total: dados?.financeiro?.totais?.pagamentoDivergente || 0 },
              { key: 'semGuia', label: 'Sem Guia Convenio', total: dados?.financeiro?.totais?.semGuia || 0 }
            ].map(tab => (
              <button
                key={tab.key}
                className={`sub-tab ${subTabFinanceiro === tab.key ? 'active' : ''}`}
                onClick={() => setSubTabFinanceiro(tab.key)}
              >
                {tab.label}
                <span className="tab-count">({tab.total})</span>
              </button>
            ))}
          </div>

          {subTabFinanceiro === 'pagamentoDivergente'
            ? renderPagamentoDivergenteTable(dados?.financeiro?.itens?.pagamentoDivergente || [])
            : renderSolicitacaoTable(dados?.financeiro?.itens?.[subTabFinanceiro] || [])
          }
        </div>,
        dados?.financeiro?.criticas || 0,
        dados?.financeiro?.alertas || 0
      )}

      {/* 4. Entregas Pendentes */}
      {renderSection('entregas', 4, 'Entregas Pendentes',
        <div>
          <div className="sub-tabs">
            {[
              { key: 'prontosSemEntrega', label: 'Prontos sem Entrega', total: dados?.entregas?.totais?.prontosSemEntrega || 0 },
              { key: 'labApoio', label: 'Lab Apoio Pendente', total: dados?.entregas?.totais?.labApoio || 0 }
            ].map(tab => (
              <button
                key={tab.key}
                className={`sub-tab ${subTabEntregas === tab.key ? 'active' : ''}`}
                onClick={() => setSubTabEntregas(tab.key)}
              >
                {tab.label}
                <span className="tab-count">({tab.total})</span>
              </button>
            ))}
          </div>

          {renderSolicitacaoTable(
            dados?.entregas?.itens?.[subTabEntregas] || [],
            true, true,
            subTabEntregas === 'prontosSemEntrega' ? 'Dias Pronto' : 'Dias Pendente'
          )}
        </div>,
        0,
        dados?.entregas?.alertas || 0
      )}

      {/* 5. Cadastro Incompleto */}
      {renderSection('cadastro', 5, 'Cadastro Incompleto',
        <div>
          <div className="mini-cards">
            {[
              { key: 'semTelefone', label: 'Sem Telefone', total: dados?.cadastro?.totais?.semTelefone || 0, color: 'blue' },
              { key: 'semCPF', label: 'Sem CPF', total: dados?.cadastro?.totais?.semCPF || 0, color: 'blue' },
              { key: 'semNascimento', label: 'Sem Nascimento', total: dados?.cadastro?.totais?.semNascimento || 0, color: 'blue' },
              { key: 'bloqueados', label: 'Bloqueado Ativo', total: dados?.cadastro?.totais?.bloqueados || 0, color: 'orange' }
            ].map(card => (
              <div
                key={card.key}
                className={`mini-card ${card.color} ${subTabCadastro === card.key ? 'active' : ''}`}
                onClick={() => setSubTabCadastro(card.key)}
              >
                <div className="mini-card-value">{card.total}</div>
                <div className="mini-card-label">{card.label}</div>
              </div>
            ))}
          </div>

          {renderPacienteTable(dados?.cadastro?.itens?.[subTabCadastro] || [])}
        </div>,
        0,
        dados?.cadastro?.alertas || 0,
        dados?.cadastro?.info || 0
      )}

      {/* 6. Notificacoes Pendentes */}
      {renderSection('notificacoes', 6, 'Notificacoes Pendentes',
        <div>
          <div className="mini-cards">
            {[
              { key: 'smsPendente', label: 'SMS Pendente', items: dados?.notificacoes?.itens?.smsPendente, color: 'orange' },
              { key: 'semSenha', label: 'Sem Senha Online', items: dados?.notificacoes?.itens?.semSenha, color: 'blue' }
            ].map(card => (
              <div
                key={card.key}
                className={`mini-card ${card.color} ${subTabNotificacoes === card.key ? 'active' : ''}`}
                onClick={() => setSubTabNotificacoes(card.key)}
              >
                <div className="mini-card-value">{(card.items || []).length}</div>
                <div className="mini-card-label">{card.label}</div>
              </div>
            ))}
          </div>

          {renderSolicitacaoTable(dados?.notificacoes?.itens?.[subTabNotificacoes] || [])}
        </div>,
        0,
        dados?.notificacoes?.alertas || 0,
        dados?.notificacoes?.info || 0
      )}

      {/* 7. Integracao WMI/PDF */}
      {renderSection('integracao', 7, 'Integracao WMI/PDF',
        <div>
          <div className="sub-tabs">
            {[
              { key: 'semAssinatura', label: 'Sem Assinatura', items: dados?.integracao?.itens?.semAssinatura },
              { key: 'pdfSemResultado', label: 'PDF sem Resultado Local', items: dados?.integracao?.itens?.pdfSemResultado }
            ].map(tab => (
              <button
                key={tab.key}
                className={`sub-tab ${subTabIntegracao === tab.key ? 'active' : ''}`}
                onClick={() => setSubTabIntegracao(tab.key)}
              >
                {tab.label}
                <span className="tab-count">({(tab.items || []).length})</span>
              </button>
            ))}
          </div>

          {renderSolicitacaoTable(
            dados?.integracao?.itens?.[subTabIntegracao] || [],
            true
          )}
        </div>,
        0,
        dados?.integracao?.alertas || 0,
        dados?.integracao?.info || 0
      )}

      {/* 8. Padronizacao de Nomes */}
      {renderSection('padronizacao', 8, 'Padronizacao de Nomes de Exames',
        <div>
          <div className="sub-tabs">
            {[
              { key: 'comSufixoLocal', label: 'Sufixo de Local', items: dados?.padronizacao?.itens?.comSufixoLocal },
              { key: 'comMaterialNome', label: 'Material no Nome', items: dados?.padronizacao?.itens?.comMaterialNome },
              { key: 'comLabRede', label: 'Lab Rede no Nome', items: dados?.padronizacao?.itens?.comLabRede }
            ].map(tab => (
              <button
                key={tab.key}
                className={`sub-tab ${subTabPadronizacao === tab.key ? 'active' : ''}`}
                onClick={() => setSubTabPadronizacao(tab.key)}
              >
                {tab.label}
                <span className="tab-count">({(tab.items || []).length})</span>
              </button>
            ))}
          </div>

          {(() => {
            const items = dados?.padronizacao?.itens?.[subTabPadronizacao] || [];
            if (items.length === 0) return <div className="incons-empty">Nenhum item encontrado</div>;
            return (
              <div className="incons-table-container">
                <table className="incons-table">
                  <thead>
                    <tr>
                      <th>Codigo</th>
                      <th>Nome Atual</th>
                      <th>Nome Sugerido</th>
                      <th>{subTabPadronizacao === 'comSufixoLocal' ? 'Sufixo' : subTabPadronizacao === 'comMaterialNome' ? 'Material' : 'Tipo'}</th>
                      <th>Exames</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => {
                      let nomeLimpo = item.NOME_ATUAL || '';
                      nomeLimpo = nomeLimpo.replace(/\s*-\s*(ITA|HP|CAM|PED|LAG|LAGOA)\s*$/i, '');
                      nomeLimpo = nomeLimpo.replace(/\s+LAB\s+REDE\b.*$/i, '');
                      nomeLimpo = nomeLimpo.replace(/\s*\((SANGUE|SORO|PLASMA)\)\s*$/i, '');
                      nomeLimpo = nomeLimpo.replace(/[\s,\-]+$/, '').trim();
                      nomeLimpo = nomeLimpo.toLowerCase().replace(/(^|[\s,/(-])([a-zA-Z])/g, (m, pre, c) => pre + c.toUpperCase());
                      return (
                        <tr key={i}>
                          <td><code>{item.LAUDO_ID}</code></td>
                          <td>{item.NOME_ATUAL}</td>
                          <td style={{ color: '#2e7d32', fontWeight: 500 }}>{nomeLimpo}</td>
                          <td>
                            <span className="badge info">{item.SUFIXO || item.MATERIAL || 'LAB REDE'}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{item.QTD_EXAMES}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>,
        0,
        0,
        dados?.padronizacao?.info || 0
      )}

      {/* Guia de Referencia */}
      <div className="incons-help-section">
        <h2 className="incons-help-title">Guia de Referencia</h2>
        <p className="incons-help-intro">
          Abaixo esta a explicacao de cada tipo de inconsistencia detectada, com exemplos praticos e a acao recomendada para correcao.
        </p>

        <div className="incons-help-item" id="help-statusExames">
          <h3>1. Status de Exames Parados</h3>
          <p className="help-description">
            Detecta exames que estao em um determinado status ha mais tempo do que o esperado.
            Um exame "parado" indica que alguma etapa do fluxo nao foi concluida: pode ser que o resultado nao foi digitado,
            nao foi conferido, ou nao foi liberado pelo responsavel tecnico.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Parciais (M) &gt; 7 dias</strong>
              <span className="help-severity critica">Critica</span>
              <p>
                O exame tem resultado parcial (parte dos parametros digitados) mas nao foi completado ha mais de 7 dias.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Hemograma solicitado em 01/01 com apenas eritrograma digitado.
                Faltam leucograma e plaquetas. Ja se passaram 10 dias sem completar.
              </div>
              <div className="help-action">Verificar com o setor tecnico por que o resultado nao foi completado (amostra perdida, recoleta necessaria, equipamento parado).</div>
            </div>
            <div className="help-subtype">
              <strong>Pendentes (P) &gt; 15 dias</strong>
              <span className="help-severity critica">Critica</span>
              <p>
                Exame solicitado ha mais de 15 dias sem nenhum resultado digitado. O status continua como "Pendente".
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> TSH solicitado em 10/01, hoje e 28/01 e nenhum resultado foi digitado.
                O paciente pode estar esperando o resultado.
              </div>
              <div className="help-action">Verificar se a amostra foi coletada, se foi enviada para lab de apoio, ou se houve problema na fase pre-analitica.</div>
            </div>
            <div className="help-subtype">
              <strong>Coletados (C) &gt; 5 dias</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Material foi coletado ha mais de 5 dias mas nenhum resultado foi digitado ainda.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Sangue coletado em 05/01 para exame de glicose, mas ate 12/01 ninguem digitou o resultado.
              </div>
              <div className="help-action">Verificar se a amostra foi processada no equipamento e se o resultado esta aguardando digitacao.</div>
            </div>
            <div className="help-subtype">
              <strong>Digitados (D) &gt; 3 dias</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Resultado ja foi digitado ha mais de 3 dias mas nao foi liberado pelo responsavel tecnico.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Resultado de colesterol digitado na segunda-feira, ja e quinta e ninguem liberou.
                O paciente nao consegue acessar online.
              </div>
              <div className="help-action">Solicitar ao bioquimico responsavel que confira e libere o resultado.</div>
            </div>
            <div className="help-subtype">
              <strong>Falta Conferir (F) &gt; 2 dias</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Resultado esta aguardando conferencia do responsavel tecnico ha mais de 2 dias.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Exame de urina com resultado digitado e marcado para conferencia, mas o bioquimico ainda nao revisou.
              </div>
              <div className="help-action">Alertar o responsavel tecnico para revisar e liberar os resultados pendentes de conferencia.</div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-divergencias">
          <h3>2. Divergencias de Status</h3>
          <p className="help-description">
            Detecta contradicoes entre o status da solicitacao (RESULTADO_LIBERADO) e o status individual dos exames.
            Essas divergencias geralmente indicam que alguem alterou um status manualmente sem atualizar o outro,
            ou houve falha no sistema ao sincronizar os estados.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Liberado sem exame pronto</strong>
              <span className="help-severity critica">Critica</span>
              <p>
                A solicitacao esta marcada como "Resultado Liberado" (RESULTADO_LIBERADO = T),
                porem nenhum dos seus exames tem status de pronto (D, L, I, E ou A).
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Protocolo 123456 com 3 exames, todos com status "P" (Pendente),
                mas a solicitacao consta como liberada. O paciente pode tentar acessar online e nao encontrar resultados.
              </div>
              <div className="help-action">Verificar se a liberacao foi feita por engano ou se os exames realmente foram concluidos e o status nao foi atualizado.</div>
            </div>
            <div className="help-subtype">
              <strong>Exame pronto sem liberacao</strong>
              <span className="help-severity critica">Critica</span>
              <p>
                Todos os exames da solicitacao estao com status de pronto, mas a solicitacao nao foi marcada como liberada.
                O paciente nao consegue acessar os resultados online.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Protocolo 789012 com hemograma e glicose, ambos com status "I" (Impresso),
                mas RESULTADO_LIBERADO = F. O paciente recebeu o QR code mas ao acessar nao ve nada.
              </div>
              <div className="help-action">Liberar o resultado da solicitacao para que o paciente possa acessar online.</div>
            </div>
            <div className="help-subtype">
              <strong>Resultado sem status</strong>
              <span className="help-severity critica">Critica</span>
              <p>
                Exame possui dados digitados na tabela de resultados, porem seu status ainda consta como "P" (Pendente).
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Glicose com valor "98 mg/dL" salvo na tabela de resultados, mas o status do exame continua "P".
                O resultado existe mas nao aparece para conferencia nem liberacao.
              </div>
              <div className="help-action">Atualizar o status do exame para "D" (Digitado) para que entre no fluxo de conferencia.</div>
            </div>
            <div className="help-subtype">
              <strong>Status sem resultado</strong>
              <span className="help-severity critica">Critica</span>
              <p>
                Exame tem status de liberado (L, I, E ou A), porem nao possui nenhum dado de resultado cadastrado
                e tambem nao tem PDF de laudo vinculado.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Exame de PSA com status "I" (Impresso), mas sem nenhum valor na tabela de resultados
                e sem PDF de laudo. O paciente ve o exame como "pronto" mas nao ha conteudo.
              </div>
              <div className="help-action">Verificar se o resultado foi perdido, se precisa ser redigitado, ou se o status foi alterado incorretamente.</div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-financeiro">
          <h3>3. Financeiro</h3>
          <p className="help-description">
            Detecta problemas no setor financeiro: titulos a receber vencidos, solicitacoes sem guia de convenio
            vinculada e solicitacoes com valor mas sem registro de pagamento.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Titulos vencidos</strong>
              <span className="help-severity critica">Critica (90+ dias) / Alerta</span>
              <p>
                Contas a receber com situacao "Aberto" cuja data de vencimento ja passou.
                O grafico mostra o aging por faixa: 1-30 dias, 31-60, 61-90 e acima de 90 dias.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Titulo de R$ 150,00 referente a convenio XYZ, vencido em 15/11,
                com saldo de R$ 150,00 ainda em aberto. Ja se passaram 60 dias sem recebimento.
              </div>
              <div className="help-action">Entrar em contato com o convenio/paciente para cobranca. Titulos acima de 90 dias devem ser priorizados.</div>
            </div>
            <div className="help-subtype">
              <strong>Sem guia de convenio</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Solicitacoes que nao possuem nenhuma guia de convenio vinculada. Sem a guia,
                nao e possivel faturar o convenio corretamente.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Atendimento do paciente Maria Silva pelo convenio Unimed, mas ninguem cadastrou
                a guia com o numero de autorizacao. O faturamento mensal ficara incompleto.
              </div>
              <div className="help-action">Verificar se a solicitacao e particular (nao precisa de guia) ou se a guia precisa ser cadastrada.</div>
            </div>
            <div className="help-subtype">
              <strong>Sem pagamento</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Solicitacoes com valor total maior que zero, porem sem nenhum registro de pagamento vinculado
                (via guia de convenio).
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Solicitacao particular no valor de R$ 80,00, mas nao ha registro de que o paciente pagou.
                Pode ser falta de registro no sistema ou inadimplencia.
              </div>
              <div className="help-action">Verificar se o pagamento foi realizado e nao registrado, ou se ha pendencia de cobranca.</div>
            </div>
            <div className="help-subtype">
              <strong>Pagamento divergente</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Solicitacoes onde o valor total pago (soma dos pagamentos) e diferente do valor cobrado (TOTAL da solicitacao).
                Pode ser pagamento parcial (faltou pagar) ou excedente (pagou a mais).
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Solicitacao com valor cobrado de R$ 210,00 mas pagamento registrado de R$ 200,00.
                Diferenca de R$ 10,00 pode ser desconto nao registrado ou valor pendente.
              </div>
              <div className="help-action">Verificar se a diferenca e intencional (desconto, arredondamento) ou se precisa de complemento.</div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-entregas">
          <h3>4. Entregas Pendentes</h3>
          <p className="help-description">
            Detecta resultados que ja estao prontos mas ainda nao foram entregues ao paciente,
            seja presencialmente ou por meio eletronico. Tambem monitora exames de laboratorio de apoio
            que estao demorando mais que o esperado.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Prontos sem entrega &gt; 7 dias</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Exames com resultado pronto (status D, L, I ou A) ha mais de 7 dias, mas sem registro de entrega.
                O status nao e "E" (Entregue), indicando que o paciente pode nao ter recebido.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Hemograma com resultado liberado em 03/01, hoje e 12/01 e o status ainda e "I" (Impresso).
                O paciente nao retirou o resultado nem acessou online.
              </div>
              <div className="help-action">Entrar em contato com o paciente para informar que os resultados estao disponiveis. Considerar envio via WhatsApp.</div>
            </div>
            <div className="help-subtype">
              <strong>Lab apoio pendente &gt; 10 dias</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Exames enviados para laboratorio de apoio (terceirizado) ha mais de 10 dias sem resultado.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Exame de cariótipo enviado para lab de apoio em 02/01, ja se passaram 12 dias e nao chegou resultado.
                O paciente liga cobrando e nao temos previsao.
              </div>
              <div className="help-action">Contatar o laboratorio de apoio para cobrar o resultado e dar previsao ao paciente.</div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-cadastro">
          <h3>5. Cadastro Incompleto</h3>
          <p className="help-description">
            Detecta pacientes com dados cadastrais incompletos que tiveram solicitacoes no periodo.
            Dados faltantes dificultam o contato com o paciente, emissao de notas fiscais e o cumprimento
            de obrigacoes regulatorias.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Sem telefone</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                Paciente nao possui nenhum telefone cadastrado (telefone fixo, celular e WhatsApp estao todos vazios).
                Impossibilita o envio de SMS, WhatsApp e contato telefonico.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Paciente Joao da Silva tem exames prontos, mas nao temos telefone para avisar.
                Ele so vai saber quando voltar presencialmente ao laboratorio.
              </div>
              <div className="help-action">Na proxima visita do paciente, solicitar atualizacao do cadastro com telefone de contato.</div>
            </div>
            <div className="help-subtype">
              <strong>Sem CPF</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                Paciente sem CPF cadastrado. O CPF e necessario para emissao de notas fiscais,
                identificacao unica e integracoes com outros sistemas.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Paciente Ana Souza fez exames pelo SUS e nao foi solicitado CPF no cadastro.
                Se precisar emitir nota fiscal ou cruzar dados, nao sera possivel.
              </div>
              <div className="help-action">Solicitar o CPF na proxima visita ou atualizar via contato telefonico.</div>
            </div>
            <div className="help-subtype">
              <strong>Sem data de nascimento</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                Paciente sem data de nascimento cadastrada. A data e importante para calculo de idade
                nos laudos (valores de referencia variam por faixa etaria).
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Resultado de hemoglobina de 11 g/dL - sem saber a idade, nao da para comparar
                com a referencia correta (crianca x adulto x idoso).
              </div>
              <div className="help-action">Atualizar o cadastro do paciente com a data de nascimento.</div>
            </div>
            <div className="help-subtype">
              <strong>Bloqueado ativo</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Paciente marcado como "bloqueado" no sistema, porem com solicitacoes recentes.
                Pode indicar inadimplencia ou outra restricao que deveria ter impedido o atendimento.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Paciente Carlos Oliveira bloqueado por debito de R$ 200,00, mas fez exames na semana passada.
                O atendimento nao deveria ter sido realizado sem quitar a pendencia.
              </div>
              <div className="help-action">Verificar o motivo do bloqueio e se o atendimento foi autorizado. Regularizar a situacao financeira.</div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-notificacoes">
          <h3>6. Notificacoes Pendentes</h3>
          <p className="help-description">
            Detecta falhas no envio de notificacoes ao paciente. Inclui SMS nao enviados para resultados prontos
            e solicitacoes liberadas sem senha de acesso online.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>SMS pendente</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Solicitacao com exames prontos, marcada para envio de SMS (ENVIAR_SMS = S),
                mas o SMS ainda nao foi enviado (SMS_COMPLETO_ENVIADO = F).
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Paciente Maria tem hemograma e glicose prontos desde 05/01.
                Esta marcado para receber SMS, mas o sistema nao enviou. Ela nao sabe que ja pode retirar.
              </div>
              <div className="help-action">Verificar o servico de envio de SMS. Reenviar manualmente se necessario ou avisar o paciente por outro meio.</div>
            </div>
            <div className="help-subtype">
              <strong>Sem senha online</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                Solicitacao com resultado liberado (RESULTADO_LIBERADO = T), mas sem senha de acesso
                ao portal online (SENHA_SLINE vazia). O paciente nao consegue acessar seus resultados pela internet.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Protocolo 456789 liberado, mas sem SENHA_SLINE. O paciente recebeu o QR code
                no comprovante, mas ao tentar acessar o link, nao consegue entrar pois nao ha senha gerada.
              </div>
              <div className="help-action">Gerar a senha online para a solicitacao ou verificar por que o sistema nao gerou automaticamente.</div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-padronizacao">
          <h3>8. Padronizacao de Nomes de Exames</h3>
          <p className="help-description">
            Identifica laudos cujos nomes contem sufixos internos do sistema (local, material, lab de apoio)
            que deveriam ser removidos para apresentacao limpa ao paciente. Esses sufixos sao uteis internamente
            mas nao devem aparecer nos laudos impressos ou PDFs.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Sufixo de Local (-ITA, -HP, -CAM, -PED)</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                O nome do laudo contem um sufixo indicando a unidade de origem (ITA=Itapecerica, HP=Hospital,
                CAM=Camacho, PED=Pedra do Indaia). Este sufixo e removido automaticamente na exibicao.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> "UREIA, DOSAGEM (SANGUE)-ITA" aparece como "Ureia, Dosagem" no laudo do paciente.
              </div>
            </div>
            <div className="help-subtype">
              <strong>Material no Nome (SANGUE, SORO, PLASMA)</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                O nome inclui o tipo de material entre parenteses. O material ja e informado separadamente
                no campo "Material" do laudo, tornando redundante no nome.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> "HEMOGRAMA COMPLETO (SANGUE)" aparece como "Hemograma Completo" no laudo.
              </div>
            </div>
            <div className="help-subtype">
              <strong>Lab Rede no Nome</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                O nome inclui "LAB REDE" indicando que o exame e processado via laboratorio de apoio.
                Esta informacao ja e apresentada separadamente no laudo.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> "T4 LIVRE, DOSAGEM LAB REDE" aparece como "T4 Livre, Dosagem" no laudo.
              </div>
            </div>
          </div>
        </div>

        <div className="incons-help-item" id="help-integracao">
          <h3>7. Integracao WMI/PDF</h3>
          <p className="help-description">
            Detecta problemas na integracao com o sistema WMI/LifeSys (portal de resultados online)
            e na consistencia entre laudos em PDF e dados locais do sistema.
          </p>
          <div className="help-subtypes">
            <div className="help-subtype">
              <strong>Sem assinatura</strong>
              <span className="help-severity alerta">Alerta</span>
              <p>
                Exames com resultado pronto (status D, L, I, E ou A), porem sem assinatura do responsavel tecnico.
                A assinatura e obrigatoria por regulamentacao sanitaria em laudos laboratoriais.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Exame de colesterol liberado e impresso, mas sem o nome do bioquimico responsavel
                no campo de assinatura. Se o paciente apresentar o laudo a um medico, pode ser questionada a validade.
              </div>
              <div className="help-action">O responsavel tecnico deve assinar os laudos pendentes. Verificar se ha bioquimico designado para os exames.</div>
            </div>
            <div className="help-subtype">
              <strong>PDF sem resultado local</strong>
              <span className="help-severity info">Informativa</span>
              <p>
                Exame possui PDF de laudo (geralmente vindo de laboratorio de apoio), mas nao tem dados
                de resultado cadastrados localmente no sistema. Isto significa que o resultado existe em PDF
                mas nao esta disponivel para consulta estruturada.
              </p>
              <div className="help-example">
                <strong>Exemplo:</strong> Exame de cultura com PDF recebido do lab de apoio, mas os dados nao foram
                transcritos para o sistema local. O medico so consegue ver o resultado pelo PDF, nao pelo sistema.
              </div>
              <div className="help-action">Avaliar se e necessario transcrever o resultado do PDF para o sistema local, ou se o PDF ja e suficiente.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PainelInconsistencias;
